import { Field, Label, Description, Switch as HSwitch } from "@headlessui/react";
import type { ReactNode } from "react";

interface SwitchProps {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  "aria-label"?: string;
}

const toggleClass = (checked: boolean) => `
  relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full
  border-2 border-transparent transition-colors duration-150
  focus-ring disabled:opacity-50 disabled:cursor-not-allowed
  ${checked ? "bg-accent" : "bg-zinc-300 dark:bg-zinc-700"}
`;

const knobClass = (checked: boolean) => `
  pointer-events-none inline-block h-4 w-4 transform rounded-full
  bg-white shadow-sm ring-0 transition-transform duration-150
  ${checked ? "translate-x-4" : "translate-x-0"}
`;

export const Switch = ({
  checked,
  onCheckedChange,
  label,
  description,
  disabled,
  "aria-label": ariaLabel,
}: SwitchProps) => {
  if (!label && !description) {
    return (
      <HSwitch
        checked={checked}
        onChange={onCheckedChange}
        disabled={disabled}
        aria-label={ariaLabel}
        className={toggleClass(checked)}
      >
        <span aria-hidden="true" className={knobClass(checked)} />
      </HSwitch>
    );
  }

  return (
    <Field disabled={disabled} className="flex items-start justify-between gap-4">
      <div className="flex-1 min-w-0">
        {label && (
          <Label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 cursor-pointer">
            {label}
          </Label>
        )}
        {description && (
          <Description className="block text-xs text-zinc-500 dark:text-zinc-400 mt-1 leading-relaxed">
            {description}
          </Description>
        )}
      </div>
      <HSwitch
        checked={checked}
        onChange={onCheckedChange}
        className={toggleClass(checked)}
      >
        <span aria-hidden="true" className={knobClass(checked)} />
      </HSwitch>
    </Field>
  );
};
